import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Tag, ArrowLeft } from 'lucide-react';
import BlogCard from '../components/BlogCard';
import { getAllPosts } from '../lib/blog';
import type { BlogPostMetadata } from '../types/blog';

export default function TagPage() {
  const { tag } = useParams<{ tag: string }>();
  const [posts, setPosts] = useState<BlogPostMetadata[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const allPosts = await getAllPosts();
        const filtered = allPosts.filter(post => 
          post.tags.some(t => t.toLowerCase() === tag?.toLowerCase())
        );
        setPosts(filtered);
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tag) {
      loadPosts();
      document.title = `${tag} | Blogg - Beredskapshubben`;
    }
  }, [tag]);

  if (loading) {
    return (
      <div className="container mx-auto max-w-screen-xl px-4 py-12">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
            <p className="text-muted-foreground">Laddar artiklar...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-screen-xl px-4 py-8">
      {/* Back button */}
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-[#f2f2f2] transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Tillbaka till alla artiklar
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-[#ed8b00]">
          <Tag className="h-5 w-5" />
          <span className="text-lg font-semibold">{tag}</span>
        </div>
        <h1 className="text-4xl font-bold text-[#f2f2f2]">
          Artiklar taggade med "{tag}"
        </h1>
        <p className="mt-2 text-muted-foreground">
          {posts.length} {posts.length === 1 ? 'artikel' : 'artiklar'} hittades
        </p>
      </div>

      {/* Posts grid */}
      {posts.length > 0 ? (
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground text-lg">
            Inga artiklar hittades med taggen "{tag}"
          </p>
        </div>
      )}
    </div>
  );
}
